/**
 * API Configuration
 * Central configuration for API client
 */

/**
 * API configuration interface
 */
export interface ApiConfig {
  BASE_URL: string;
  TIMEOUT: number;
  USE_MOCK: boolean;
}

export const API_CONFIG: ApiConfig = {
  BASE_URL: import.meta.env.VITE_API_BASE_URL || 'https://api.staging.digitimax.ir',
  TIMEOUT: 30000,
  USE_MOCK: import.meta.env.VITE_USE_MOCK_API === 'true'
};

export const API_ENDPOINTS = {
  // Auth
  AUTH: {
    LOGIN: '/api/users/login',
    REQUEST_OTP: '/api/users/request-otp',
    REFRESH: '/api/users/refresh',
    REGISTER: '/api/users/register',
    LOGOUT: (id: string) => `/api/users/${id}/logout`
  },

  // Users
  USERS: {
    LIST: '/api/users',
    DETAIL: (id: string) => `/api/users/${id}`,
    CREATE: '/api/users/create',
    UPDATE: (id: string) => `/api/users/${id}`,
    ACTIVATE: (id: string) => `/api/users/${id}/activate`,
    DEACTIVATE: (id: string) => `/api/users/${id}/deactivate`,
    CHANGE_PASSWORD: (id: string) => `/api/users/${id}/change-password`
  },

  // Products
  PRODUCTS: {
    LIST: '/api/products',
    ADMIN_LIST: '/api/products/admin',
    DETAIL: (id: string) => `/api/products/${id}`,
    BY_SLUG: (slug: string) => `/api/products/by-slug/${slug}`,
    CREATE: '/api/products',
    UPDATE: (id: string) => `/api/products/${id}`,
    DELETE: (id: string) => `/api/products/${id}`
  },

  // Categories
  CATEGORIES: {
    LIST: '/api/categories',
    TREE: '/api/categories/tree',
    DETAIL: (id: string) => `/api/categories/${id}`,
    CREATE: '/api/categories',
    UPDATE: (id: string) => `/api/categories/${id}`,
    DELETE: (id: string) => `/api/categories/${id}`,
    IMAGE: (id: string) => `/api/categories/${id}/image`
  },

  // Attributes
  ATTRIBUTES: {
    BY_CATEGORY: (categoryId: string, onlyFilterable?: boolean) => {
      const base = `/api/attributes/by-category/${categoryId}`;
      return onlyFilterable ? `${base}?onlyFilterable=true` : base;
    },
    CREATE: '/api/attributes',
    UPDATE: (id: string) => `/api/attributes/${id}`,
    DELETE: (id: string) => `/api/attributes/${id}`
  },

  // Roles
  ROLES: {
    LIST: '/api/roles',
    DETAIL: (id: string) => `/api/roles/${id}`,
    CREATE: '/api/roles',
    UPDATE: (id: string) => `/api/roles/${id}`,
    DELETE: (id: string) => `/api/roles/${id}`,
    PERMISSIONS: (id: string) => `/api/roles/${id}/permissions`
  },

  // Permissions
  PERMISSIONS: {
    LIST: '/api/permissions',
    DETAIL: (id: string) => `/api/permissions/${id}`
  },

  // Orders
  ORDERS: {
    CURRENT: '/api/orders/current',
    COMPLETED: '/api/orders/completed',
    CANCELLED: '/api/orders/cancelled',
    RETURNED: '/api/orders/returned',
    DETAIL: (id: string) => `/api/orders/${id}`,
    UPDATE_STATUS: (id: string) => `/api/orders/${id}/status`,
    ADD_TRACKING: (id: string) => `/api/orders/${id}/tracking`,
    ADMIN_CANCEL: (id: string) => `/api/orders/${id}/admin-cancel`,
    TRACK: (orderNumber: string) => `/api/orders/track/${orderNumber}`
  },

  // Coupons
  COUPONS: {
    LIST: '/api/coupons',
    DETAIL: (id: string) => `/api/coupons/${id}`,
    CREATE: '/api/coupons',
    DELETE: (id: string) => `/api/coupons/${id}`
  },

  // Special Sales
  SPECIAL_SALES: {
    LIST: '/api/special-sales',
    DETAIL: (id: string) => `/api/special-sales/${id}`,
    CREATE: '/api/special-sales',
    UPDATE: (id: string) => `/api/special-sales/${id}`,
    DELETE: (id: string) => `/api/special-sales/${id}`
  },

  // Article Categories
  ARTICLE_CATEGORIES: {
    LIST: '/api/article-categories',
    TREE: '/api/article-categories/tree',
    DETAIL: (id: string) => `/api/article-categories/${id}`,
    CREATE: '/api/article-categories',
    UPDATE: (id: string) => `/api/article-categories/${id}`,
    DELETE: (id: string) => `/api/article-categories/${id}`
  },

  // Articles
  ARTICLES: {
    LIST: '/api/articles',
    DETAIL: (id: string) => `/api/articles/${id}`,
    BY_SLUG: (slug: string) => `/api/articles/by-slug/${slug}`,
    BY_CATEGORY: (categoryId: string) => `/api/articles/category/${categoryId}`,
    CREATE: '/api/articles',
    UPDATE: (id: string) => `/api/articles/${id}`,
    DELETE: (id: string) => `/api/articles/${id}`,
    PUBLISH: (id: string) => `/api/articles/${id}/publish`,
    IMAGE: (id: string) => `/api/articles/${id}/image`,
    MEDIA_UPLOAD: (id: string) => `/api/articles/${id}/media/upload`,
    MEDIA: (id: string) => `/api/articles/${id}/media`
  },

  // Addresses
  ADDRESSES: {
    DETAIL: (id: string) => `/api/addresses/${id}`,
    BY_USER: (userId: string) => `/api/addresses/user/${userId}`
  },

  // Reviews
  REVIEWS: {
    ADMIN: '/api/reviews/admin',
    BY_PRODUCT: (productId: string) => `/api/reviews/product/${productId}`,
    RATING: (productId: string) => `/api/reviews/product/${productId}/rating`,
    APPROVE: (reviewId: string) => `/api/reviews/${reviewId}/approve`,
    DELETE: (reviewId: string) => `/api/reviews/${reviewId}`
  }
} as const;

export const QUERY_KEYS = {
  AUTH: {
    USER: 'auth-user',
    SESSION: 'auth-session'
  },
  USERS: {
    LIST: 'users-list',
    DETAIL: (id: string) => ['users-detail', id] as const
  },
  PRODUCTS: {
    LIST: 'products-list',
    DETAIL: (id: string) => ['products-detail', id] as const,
    BY_SLUG: (slug: string) => ['products-by-slug', slug] as const
  },
  CATEGORIES: {
    LIST: 'categories-list',
    TREE: 'categories-tree',
    DETAIL: (id: string) => ['categories-detail', id] as const
  },
  ATTRIBUTES: {
    BY_CATEGORY: (categoryId: string, onlyFilterable?: boolean) => ['attributes-by-category', categoryId, onlyFilterable] as const
  },
  ROLES: {
    LIST: 'roles-list',
    DETAIL: (id: string) => ['roles-detail', id] as const,
    PERMISSIONS: (id: string) => ['roles-permissions', id] as const
  },
  PERMISSIONS: {
    LIST: 'permissions-list',
    DETAIL: (id: string) => ['permissions-detail', id] as const
  },
  ORDERS: {
    BY_KIND: (kind: string) => ['orders-by-kind', kind] as const,
    DETAIL: (id: string) => ['orders-detail', id] as const,
    TRACK: (orderNumber: string) => ['orders-track', orderNumber] as const
  },
  COUPONS: {
    LIST: 'coupons-list',
    DETAIL: (id: string) => ['coupons-detail', id] as const
  },
  SPECIAL_SALES: {
    LIST: 'special-sales-list',
    DETAIL: (id: string) => ['special-sales-detail', id] as const
  },
  ARTICLE_CATEGORIES: {
    LIST: 'article-categories-list',
    TREE: 'article-categories-tree',
    DETAIL: (id: string) => ['article-categories-detail', id] as const
  },
  ARTICLES: {
    LIST: 'articles-list',
    DETAIL: (id: string) => ['articles-detail', id] as const,
    MEDIA: (id: string) => ['articles-media', id] as const
  },
  ADDRESSES: {
    DETAIL: (id: string) => ['addresses-detail', id] as const,
    BY_USER: (userId: string) => ['addresses-by-user', userId] as const
  },
  REVIEWS: {
    ADMIN: 'reviews-admin',
    BY_PRODUCT: (productId: string) => ['reviews-by-product', productId] as const,
    RATING: (productId: string) => ['reviews-rating', productId] as const
  }
} as const;
